import { provideSingleton } from '@bcf-vanilla-ts-v1-shared/di/provide-singleton';
import type { DeviceInfoPayload } from '@bcf-vanilla-ts-v1-utilities/device-info/types';
import type { Observable } from 'rxjs';
import { BehaviorSubject, combineLatest, filter, map, ReplaySubject } from 'rxjs';

export type Cookies = Record<string, string>;

function extractCookies(cookiesStr: string): Cookies {
  const container: Cookies = {};

  const splitedOuter: string[] = cookiesStr.split(';');
  for (const innerStr of splitedOuter) {
    const splitedInner: string[] = innerStr.split('=');
    container[splitedInner[0].trim()] = splitedInner[1];
  }

  return container;
}

export class WorkerToolkit {
  private _cookies$: ReplaySubject<Cookies> = new ReplaySubject<Cookies>(1);
  private _url$: ReplaySubject<string> = new ReplaySubject<string>(1);
  private _baseHref$: ReplaySubject<string> = new ReplaySubject<string>(1);
  private _fullUrl$: ReplaySubject<string> = new ReplaySubject<string>(1);
  private _deviceInfo$: ReplaySubject<DeviceInfoPayload> = new ReplaySubject<DeviceInfoPayload>(1);
  private _appVisibilityState$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  private _isUserActiveInApp$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  private _lang$: BehaviorSubject<string | undefined> = new BehaviorSubject<string | undefined>(undefined);

  public updateCookies(cookieStr: string): void {
    this._cookies$.next(extractCookies(cookieStr));
  }

  public updateAppVisibilityState(isVisible: boolean): void {
    this._appVisibilityState$.next(isVisible);
  }

  public updateUrls(pathname: string, fullUrl: string): void {
    this._url$.next(pathname);
    this._fullUrl$.next(fullUrl);
  }

  public updateBaseHref(baseHref: string): void {
    this._baseHref$.next(baseHref);
  }

  public updateDeviceInfo(deviceInfo: DeviceInfoPayload): void {
    this._deviceInfo$.next(deviceInfo);
  }

  public updateLang(lang: string): void {
    this._lang$.next(lang);
  }

  public updateIsUserActiveInApp(value: boolean): void {
    this._isUserActiveInApp$.next(value);
  }

  public get cookies$(): Observable<Cookies> {
    return this._cookies$.asObservable();
  }

  public get url$(): Observable<string> {
    return combineLatest([this._url$, this._baseHref$]).pipe(
      map(([url, baseHref]: [string, string]) => {
        const replaced: string = url.replace(baseHref, '');
        if (!replaced.startsWith('/')) {
          return `/${replaced}`;
        }
        return replaced;
      })
    );
  }

  public get fullUrl$(): Observable<string> {
    return this._fullUrl$;
  }

  public get lang(): string | undefined {
    return this._lang$.getValue();
  }

  public get lang$(): Observable<string> {
    return this._lang$.pipe(filter((lang: string | undefined): lang is string => Boolean(lang)));
  }

  public get deviceInfo$(): Observable<DeviceInfoPayload> {
    return this._deviceInfo$;
  }

  public get isUserActiveInApp$(): Observable<boolean> {
    return this._isUserActiveInApp$.pipe(filter((state: boolean) => state === true));
  }

  public get isUserNotActiveInApp$(): Observable<boolean> {
    return this._isUserActiveInApp$.pipe(filter((state: boolean) => state === false));
  }

  public get appVisibilityState$(): Observable<boolean> {
    return this._appVisibilityState$;
  }

  public isAppVisibilityActiveSignal(): Observable<void> {
    return this.appVisibilityState$.pipe(
      filter((state: boolean) => state === true),
      map(() => undefined)
    );
  }

  public isAppVisibilityNotActiveSignal(): Observable<void> {
    return this.appVisibilityState$.pipe(
      filter((state: boolean) => state === false),
      map(() => undefined)
    );
  }
}

export function provideWorkerToolkit(): WorkerToolkit {
  return provideSingleton(WorkerToolkit, () => new WorkerToolkit());
}
